<template>

    <div class="equipment-import">

        <heading :title="title" tag="equipment-import-title"/>

        <b-row class="justify-content-center mt-5">
            <b-col cols="4" col-offset="3">
                <b-form-file
                    v-model="file"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    :state="Boolean(file)"
                    placeholder="Select File"
                    drop-placeholder="Drop file here...">
                </b-form-file>
            </b-col>
        </b-row>

        <!-- instructions -->
        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3" class="text-center">
                <b-alert show variant="info" class="alert-plain">
                    <template v-if="!file">
                        <h3>
                            Select an .XSLX file containing your list of equipment.
                        </h3>
                        <p>
                            The file must be created from this import template:
                            <br/>
                            <b-btn variant="info" size="sm" class="mt-2"
                                   href="/import-templates/Equipment Import Template.xlsx">
                                Download template
                                <b-icon icon="download"/>
                            </b-btn>
                        </p>
                    </template>
                    <template v-else>
                        <p>
                            Please review the data below for errors.
                        </p>
                        <p>
                            After importing, you can edit individual equipment records to add any missing data.
                        </p>
                        <b-btn variant="danger" class="mr-2"
                               @click="reset()">
                            Cancel
                        </b-btn>
                        <b-btn variant="primary"
                               @click="upload()">
                            Import Equipment
                        </b-btn>
                    </template>
                </b-alert>
            </b-col>
        </b-row>

        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-alert show variant="danger" v-if="sheetHasErrors">
                    <p>The uploaded sheet has invalid data, marked in red below.<br>
                    When filling out <i>Type, Fuel Type, Exhaust Retrofit Type and Retrofit Manufacturer</i> make sure
                        to select the options from each dropdown in the template sheet.</p>
                    <p><strong>DO NOT FILL IN THESE VALUES DIRECTLY</strong></p>
                    <p>Edit the template sheet with Microsoft Excel or Google Sheets. Other spreadsheet software may not
                        support dropdowns.</p>
                </b-alert>
                <b-alert show variant="warning" v-if="sheetHasDuplicates">
                    <p>The uploaded sheet contains duplicate equipment that has already been imported (i.e. the VIN or
                        Serial Number is matching)</p>
                    <p><strong>Duplicate equipment WILL NOT be imported again.</strong>
                        You must edit the equipment manually, or delete it first before importing again.</p>
                </b-alert>
            </b-col>
        </b-row>
        <!-- data preview -->
        <b-card no-body class="mt-3"
                v-if="file">
            <b-card-body>
                <div v-for="(rows, i) in [onRoadDataRows, nonRoadDataRows]" :key="i">
                    <h3>
                        {{ rows === onRoadDataRows ? 'On-road Vehicle' : 'Non-road Equipment' }}
                    </h3>
                    <template v-if="rows.length > 1"> <!-- header row should always be present -->
                        <table class="table table-responsive table-striped my-3">
                            <tbody>
                            <tr v-for="(row,r) in rows" :key="r" :class="getRowClass(row, rows === onRoadDataRows)">
                                <template v-if="r === 0">
                                    <td v-for="(col,c) in row" :key="c">
                                        <strong>{{ col }}</strong>
                                    </td>
                                </template>
                                <template v-else>
                                    <td v-for="(field,f) in rows === onRoadDataRows ? onRoadFields : nonRoadFields"
                                        :class="getDataCellClass(row, field, rows === onRoadDataRows)"
                                        :key="f">
                                        {{ row[field] }}
                                    </td>
                                </template>
                            </tr>
                            </tbody>
                        </table>
                    </template>
                    <template v-else>
                        <p class="mb-3">No data found</p>
                    </template>
                </div>
            </b-card-body>
        </b-card>

    </div>
</template>

<script>

import Vue from 'vue'
import * as _ from 'lodash'
import XLSX from 'xlsx'
import Equipment from '@/models/Equipment'
import Api from "@/providers/Api";

/**
 * Page to import participants from a CSV file
 */
export default Vue.extend({
    name: 'EquipmentImport',
    props: {
        companyId: Number
    },
    data() {
        return {
            file: null,
            loading: false,
            dataRows: null,
            onRoadDataRows: [],
            onRoadFields: [],
            nonRoadDataRows: [],
            nonRoadFields: [],
            equipment: [],
            title: 'Import Equipment',

            retrofitManufacturerOptions: null,
            retrofitDeviceOptions: null,
            vins: [],
            serialNumbers: [],
        }
    },
    computed: {
        sheetHasErrors() {
            let error = false
            _.each(this.onRoadDataRows, (row, idx) => {
                _.each(this.onRoadFields, (field) => {
                    if (idx !== 0) {
                        error ||= this.getDataCellClass(row, field, true) === 'error-cell'
                    }
                })
            })
            _.each(this.nonRoadDataRows, (row, idx) => {
                _.each(this.nonRoadFields, (field) => {
                    if (idx !== 0) {
                        error ||= this.getDataCellClass(row, field, false) === 'error-cell'
                    }
                })
            })
            return error
        },
        sheetHasDuplicates() {
            let hasDuplicates = false
            _.each(this.onRoadDataRows, (row, idx) => {
                if (idx !== 0) {
                    hasDuplicates ||= this.getRowClass(row, true) === 'duplicate-row'
                }
            })
            _.each(this.nonRoadDataRows, (row, idx) => {
                if (idx !== 0) {
                    hasDuplicates ||= this.getRowClass(row, false) === 'duplicate-row'
                }
            })
            return hasDuplicates
        }
    },
    methods: {
        parseExcelFile() {
            this.loading = true
            let reader = new FileReader()
            reader.onload = (e) => {
                const data = e.target.result
                const workbook = XLSX.read(data, {type: 'binary'})

                // initial rows in each sheet: 0 = field names; 1 = friendly labels; 2 = explanations
                // (sheet_to_json gives 0 = friendly labels; 1 = explanations)
                // these two rows are ignored on back end - see EquipmentController::actionImport

                const onRoadRows = XLSX.utils.sheet_to_json(workbook.Sheets['On-road Vehicle'])
                this.onRoadDataRows = [_.values(onRoadRows[0])] // friendly labels
                this.onRoadFields = _.keys(onRoadRows[0])
                console.log(onRoadRows)
                _.each(onRoadRows, (r, i) => {
                    if (i > 1) {
                        this.onRoadDataRows.push(r)
                    }
                })

                const nonRoadRows = XLSX.utils.sheet_to_json(workbook.Sheets['Non-road Equipment'])
                this.nonRoadDataRows = [_.values(nonRoadRows[0])] // friendly labels
                this.nonRoadFields = _.keys(nonRoadRows[0])
                _.each(nonRoadRows, (r, i) => {
                    if (i > 1) {
                        this.nonRoadDataRows.push(r)
                    }
                })

                this.loading = false
            }
            reader.onerror = (e) => {
                this.loading = false
                alert('Error loading file')
            }
            reader.readAsBinaryString(this.file)
        },
        async upload() {
            try {
                if (!this.sheetHasErrors || await this.$bvModal.msgBoxConfirm('The uploaded sheet has format errors. Cells marked in red below will NOT be included in import.')) {
                    const nullifiedOnRoad = this.formatDataForImport(this.onRoadDataRows, this.onRoadFields, true)
                    const nullifiedNonRoad = this.formatDataForImport(this.nonRoadDataRows, this.nonRoadFields, false)
                    await this.$api.importEquipment(this, nullifiedOnRoad, nullifiedNonRoad, this.companyId)
                    this.$toasted.show('Equipment imported', {duration: 3000})
                    this.$router.replace(`/equipment/list/${this.companyId}`)
                }
            } catch (e) {
                Vue.toasted.error('Error importing equipment')
                // this.$utils.handleError(e, 'Error importing equipment')
            }
        },
        reset() {
            this.onRoadDataRows = []
            this.nonRoadDataRows = []
            this.file = null
        },
        getDataCellClass(row, field, onroad) {

            let valid = null
            if (field === 'type') {
                if (onroad) {
                    valid = Equipment.onRoadTypeOptions.map(option => option.value).includes(row['type'])
                } else {
                    valid = Equipment.nonRoadTypeOptions.map(option => option.value).includes(row['type'])
                }
            } else if (field === 'fuel_type') {
                valid = ['DIESEL', 'NON_DIESEL'].includes(row['fuel_type'])
            } else if (field === 'exhaust_retrofit_type') {
                const options = ['NONE', 'OTHER', 'DOC', 'DPF']
                // non-road diesel vehicles' exhaust_retrofit_type field is hidden on spreadsheet if hp <= 25, so make undefined valid in this case
                if (!onroad && row['fuel_type'] === 'DIESEL' && row['engine_hp'] <= 25) {
                    options.push(undefined)
                }
                valid = options.includes(row['exhaust_retrofit_type'])
            } else if (field === 'retrofit_manufacturer') {
                valid = this.retrofitManufacturerOptions.includes(row['retrofit_manufacturer']) || row['retrofit_manufacturer'] == null
            } else if (field === 'retrofit_model') {
                valid = this.retrofitDeviceOptions.includes(row['retrofit_model']) || row['retrofit_model'] == null
            } else if (field === 'vin') {
                valid = row['vin'] != null
            } else if (field === 'equipment_serial_number') {
                valid = row['equipment_serial_number'] != null
            } else {
                valid = true
            }

            return valid ? '' : 'error-cell'
        },
        getRowClass(row, onroad) {
            let isDuplicate = null
            if (onroad) {
                isDuplicate = row['vin'] !== undefined && row['vin'] !== null
                    && this.vins.includes(row['vin'].toString())

            } else {
                isDuplicate = row['equipment_serial_number'] !== undefined && row['equipment_serial_number'] !== null
                    && this.serialNumbers.includes(row['equipment_serial_number'].toString())
            }
            return isDuplicate ? 'duplicate-row' : ''
        },
        formatDataForImport(rows, fields, onroad) {
            let dataRows = _.cloneDeep(rows)

            _.each(dataRows, (row) => {

                // Rename template field names to be consistent with model
                if ('vehicle_model_year' in row) {
                    row['equipment_model_year'] = row.vehicle_model_year
                    delete row.vehicle_model_year
                }
                if ('vehicle_manufacturer' in row) {
                    row['equipment_manufacturer'] = row.vehicle_manufacturer
                    delete row.vehicle_manufacturer
                }
                if ('vehicle_model' in row) {
                    row['equipment_model'] = row.vehicle_model
                    delete row.vehicle_model
                }

                //Nullify any erroneous values to prevent backend errors
                _.each(fields, (field, idx) => {
                    if (idx !== 0 && this.getDataCellClass(row, field, onroad) === 'error-cell') {
                        row[field] = null
                    }
                })
            })
            return dataRows
        }
    },
    watch: {
        // watch for file selection
        file(newVal, oldVal) {
            if (newVal) {
                this.parseExcelFile()
            }
        }
    },
    async mounted() {
        const equipment = await Api.getAllItems(null, 'equipment')
        //Fetch the vins/serials of equipment to check for dupes
        let vins = []
        let serialNumbers = []
        _.each(equipment, (e) => {
            vins.push(e['vin'])
            serialNumbers.push(e['equipment_serial_number'])
        })
        this.vins = vins
        this.serialNumbers = serialNumbers

        const retrofitManufacturers = await Api.getAllItems(null, 'retrofit-manufacturer')
        const retrofitDevices = await Api.getAllItems(null, 'retrofit-device')
        this.retrofitManufacturerOptions = retrofitManufacturers.map(m => m.name)
        this.retrofitDeviceOptions = retrofitDevices.map(d => d.name)
    }
})

</script>

<style lang="scss">
.equipment-import {
    .table-responsive {
        display: block;
    }
}

.error-cell {
    background-color: #FFCCCC !important;
    color: red;
}

.duplicate-row {
    background-color: #FFCCAA !important;
    color: #ff6724;
}

</style>
